<template>
  <div class="document-container">
    <es-run-dc-extended-modal
      v-if="showConfirmation"
      @closeModal="cancelRun"
      @confirm="runNow"
    />
    <ul class="summary-panels wrap is-flex">
      <li
        class="document flex-item flex-item-header grow"
        style="max-width: 46%"
      >
        <div class="panel-container is-flex">
          <div class="is-size-4 has-text-weight-bold is-flex">
            CEP DC Extended Match
          </div>
          <div class="panel-subtitle has-text-grey is-flex">
            View and match household addresses for DC Extended.
          </div>
        </div>
      </li>
      <li
        class="document flex-item"
        style="width: 18%"
      >
        <div class="panel-container is-flex">
          <div
            v-if="header.startDate"
            class="is-size-4 has-text-weight-bold"
          >
            {{ header.endDate || header.startDate | formatDateMMDDYYYY('--') }}
          </div>
          <div
            v-else
            class="is-size-4 has-text-weight-bold"
          >
            --
          </div>
          <div class="panel-subtitle has-text-grey">
            Last Run Date
          </div>
        </div>
      </li>
      <li
        class="document flex-item"
        style="width: 18%"
      >
        <div class="panel-container is-flex">
          <div
            v-if="header.status"
            class="is-size-4 has-text-weight-bold"
            :title="header.status === 'PROCESSING'
              ? header.hoursLeftEstimate && header.hoursLeftEstimate + ' hours remaining' : ''"
          >
            <font-awesome-icon
              v-if="header.status === 'PROCESSING'"
              :icon="['fad', 'spinner']"
              spin
            />
              {{
                header.status === 'PROCESSING'
                  ? 'In Progress'
                  : capitalizeFirstLetter(header.status)
              }}
          </div>
          <div
            v-else
            class="is-size-4 has-text-weight-bold"
          >
            --
          </div>
          <div class="panel-subtitle has-text-grey">
            Status
          </div>
        </div>
      </li>
      <li
        class="document flex-item"
        style="width: 18%"
      >
        <div class="panel-container is-flex">
          <div
            v-if="header.startDate"
            class="is-size-4 has-text-weight-bold"
          >
            {{ matchesQueued }}
          </div>
          <div
            v-else
            class="is-size-4 has-text-weight-bold"
          >
            --
          </div>
          <div class="panel-subtitle has-text-grey">
            Eligible Address Count
          </div>
        </div>
      </li>
    </ul>
    <div
      class="columns is-vcentered card-container"
      style="margin-bottom: 0"
    >
      <div class="column has-text-left has-text-grey match-result-text">
        Match Results
      </div>
      <div>
        <div
          v-if="hasModule('DRMCH:MATCH:OPTIONS:ADDRESS:RUN')"
          style="padding: 1rem 0rem"
        >
          <div
            v-if="header.status === 'PROCESSING'
              || header.status === 'QUEUED'"
          >
            <button
              class="button is-primary"
              @click="refresh"
            >
              Refresh
            </button>
          </div>
          <div v-else>
            <div
              v-if="!this.contextMode"
              class="column has-text-right"
            >
              <button
                class="button is-primary"
                @click="askConfirm('S')"
              >
                Run Now
              </button>
            </div>
            <div>
              <div
                class="field is-grouped flex-justify--end"
                v-if="this.contextMode
                  && !loading
                  && !loadingError"
              >
                <div
                  v-for="program in getProgramsForAddressMatch"
                  :key="program.programCode"
                  class="control is-flex flex-align--center"
                >
                  <button
                    class="button is-primary"
                    @click="askConfirm(program.programCode)"
                  >
                    Run {{ program.program.description }} Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <es-loader v-if="loading === true"/>
    <es-exception
      v-else-if="loadingError
        || header.status === 'ERROR'"
      exceptionMessage="There was an issue.
        Please try again or contact your administrator."
    />
    <es-exception
      v-else-if="!matchResults
        || this.matchesQueued === 0"
      exceptionMessage="No results were found."
    />
    <div
      v-else
      class="result-container"
    >
      <dc-extended-match
        v-for="(match, i) in matchResults"
        :key="`${i}-${match.id}`"
        :match="match"
        @refresh="refresh"
      />
      <div>
        <a
          v-if="this.pageSize < this.matchesQueued"
          @click="loadMore()"
        >
          Load More
        </a>
      </div>
    </div>
    <dc-extended-legend
      v-if="loading === false"
      class="legend"
      :lower="getProperty('ELASTIC_LOCATION-LOWER_BOUND_FOR_NEAR_MATCHES')"
      :upper="getProperty('ELASTIC_LOCATION-UPPER_BOUND_FOR_NEAR_MATCHES')"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSpinner } from '@fortawesome/pro-duotone-svg-icons';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import DcExtendedLegend from '@/components/DcExtendedLegend.vue';
import DcExtendedMatch from '@/components/DcExtendedMatch.vue';
import EsException from '@/components/EsException.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsRunDcExtendedModal from '@/components/EsRunDcExtendedModal.vue';
import BreadcrumbsManager from '@/mixins/BreadcrumbManager';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faSpinner,
  faExclamationCircle,
);

export default {
  mixins: [
    BreadcrumbsManager,
    NotificationManager,
    PermissionManager,
  ],
  components: {
    DcExtendedLegend,
    DcExtendedMatch,
    EsException,
    EsLoader,
    EsRunDcExtendedModal,
  },
  data() {
    return this.initialData();
  },
  watch: {
    pageSize(val) {
      if (val > 1) {
        this.refresh();
      }
    },
  },
  created() {
    this.setBreadcrumb([
      { text: 'Home', to: { name: 'directMatch' } },
      { text: 'Match Options', to: {} },
    ]);
  },
  mounted() {
    this.onLoad();
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'getProperty',
      'properties',
    ]),
    ...mapGetters('lookups', [
      'activeGenders',
      'activeGrades',
      'activeMatchTypes',
    ]),
    getProgramsForAddressMatch() {
      return this.programs.filter(s => s.program.usedForAddressMatch);
    },
    matchTypeId() {
      return this.activeMatchTypes.find(m => m.code === 'ADDR').id;
    },
    contextMode() {
      const identityControl = this.properties['IDENTITY_CONTROL-LIFETIME_IDS'];
      return JSON.parse(identityControl.toString().toLowerCase()) === false;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadGenders',
      'loadGrades',
      'loadMatchTypes'
    ]),
    addMatchedData(matchResult, matchResults) {
      const matchTrans = matchResult.dcxMatchTransIds;
      const addresses = [...new Set(matchTrans
        .map(t => t.student.street1
          + t.student.street2
          + t.student.city
          + t.student.stateCode
          + t.student.zip))];
      for (let i = 0; i < addresses.length; i += 1) {
        const m = {
          id: matchResult.id,
          matchTypeId: matchResult.matchTypeId,
          eligable: matchResult.targetStudent,
          matched: null,
          percent: 0,
          marker: 'nomatch',
          studentsEligible: 0,
          students: [],
          personType: this.getPersonType(matchResult.programCode),
          matchType: this.getMatchType(matchResult.programCode),
          districtName: matchResult.districtName,
          schoolName: matchResult.schoolName,
          identifiedDate: matchResult.identifiedDate,
        };
        for (let j = 0; j < matchTrans.length; j += 1) {
          const s = matchTrans[j].student;
          if (s.street1 + s.street2 + s.city + s.stateCode + s.zip === addresses[i]) {
            if (!m.matched) {
              m.matched = this.getaddress(matchTrans[j].student);
              m.percent = matchTrans[j].matchPercent.toFixed(2) * 100;
              m.marker = this.getMarker(m.percent);
            }
            s.districtName = matchTrans[j].districtName;
            s.schoolName = matchTrans[j].schoolName;
            m.students.push(this.getStudent(s));
          }
        }
        m.studentsEligible = m.students.length;
        matchResults.push(m);
      }
    },
    askConfirm(programCode) {
      this.programCode = programCode;
      this.toggleConfirmation();
    },
    cancelRun() {
      this.toggleConfirmation();
    },
    capitalizeFirstLetter(string) {
      const temp = string.toLowerCase();
      return temp[0].toUpperCase() + temp.slice(1);
    },
    getaddress(student) {
      return {
        street1: student.street1,
        street2: student.street2,
        city: student.city,
        stateCode: student.stateCode,
        zip: student.zip,
      };
    },
    getMarker(percent) {
      if (percent >= this.getProperty('ELASTIC_LOCATION-UPPER_BOUND_FOR_NEAR_MATCHES')) {
        return 'match';
      }
      return 'nearmatch';
    },
    getMatchHeader() {
      return axios.get('process-control?type=DC_EXTENDED_MATCHING')
        .then((res) => {
          this.header = res.data;
        }).catch(() => {
          this.header.status = '';
        });
    },
    getMatchType(code) {
      const mappedCode = code.includes('DCX') ? code.replace('DCX', 'S') : code;
      const program = this.programs.find(p => p.programCode === mappedCode);
      if (program) {
        return program.program.description;
      }
      return null;
    },
    getMatches() {
      this.matchResults = [];
      return axios.get('/directmatch/address/pending', {
        params: {
          pageSize: this.pageSize,
          programCode: this.programCode,
          open: true,
        },
      }).then((res) => {
        this.pending = res.data.data;
        this.matchesQueued = res.data.totalCount;
      }).catch(this.handleError);
    },
    getPersonType(code) {
      const program = this.programs.find(p => p.programCode === code);
      if (program) {
        const { personTypeCode } = program.program;
        if (personTypeCode) {
          const personType = this.userPersonTypes.find(p => p.code === personTypeCode);
          if (personType) {
            return personType.name;
          }
        }
      }
      return null;
    },
    getStudent(student) {
      const grade = this.activeGrades.find(g => g.code === student.gradeCode);
      const gender = this.activeGenders.find(g => g.code === student.genderCode);
      return {
        stateStudentId: student.stateStudentId,
        name: `${student.firstName} ${student.lastName}`,
        grade: grade !== undefined ? grade.description : student.gradeCode,
        gender: gender !== undefined ? gender.description : student.genderCode,
        dob: student.dateOfBirth,
        district: {
          code: student.districtCode,
          name: student.districtName,
        },
        school: {
          code: student.schoolCode,
          name: student.schoolName,
        },
      };
    },
    groupResults(resData) {
      const unsorted = [];
      if (resData && resData.length > 0) {
        for (let j = 0; j < resData.length; j += 1) {
          this.addMatchedData(resData[j], unsorted);
        }
      }
      return unsorted;
    },
    handleError(err) {
      const { status } = err.response;
      let apiMsg = '';
      if (status === 500) {
        apiMsg = 'Network Error';
      } else if (status === 400) {
        apiMsg = `${err.response.data.message}`;
      } else {
        apiMsg = 'Something went wrong.';
      }
      this.showNotification('danger', [apiMsg]);
      this.loadingError = true;
    },
    initialData() {
      return {
        header: {},
        loading: false,
        loadingError: false,
        matchResults: [],
        matchesQueued: 0,
        pageSize: 25,
        pending: [],
        programs: [],
        programCode: 'S*',
        showConfirmation: false,
        userPersonTypes: this.$store.state.user.userPersonTypes
          .map(item => item.personType).filter(a => a.status === true),
      };
    },
    loadMore() {
      if (this.pageSize < this.matchesQueued) {
        this.pageSize += this.pageSize;
      }
    },
    loadPrograms() {
      return axios.get(`matchtypes/${this.matchTypeId}/programs?matchEnabled=true`);
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      try {
        await Promise.all([
          this.loadGenders(),
          this.loadGrades(),
          this.getMatchHeader(),
          this.getMatches(),
          this.loadMatchTypes(),
        ]);
        const res = await this.loadPrograms();
        this.programs = res.data;
        const unsorted = this.groupResults(this.pending);
        this.matchResults = unsorted.sort((a, b) => b.percent - a.percent);
      } catch (e) {
        this.handleError(e);
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    async refresh() {
      this.loading = true;
      await Promise.all([
        this.getMatchHeader(),
        this.getMatches(),
      ]).then(() => {
        const unsorted = this.groupResults(this.pending);
        this.matchResults = unsorted.sort((a, b) => b.percent - a.percent);
      }).catch(this.handleError)
        .finally(() => {
          this.loading = false;
        });
    },
    async runNow() {
      this.toggleConfirmation();
      this.loading = true;
      try {
        const res = await axios.post('/directmatch/address', null, {
          params: {
            programCode: this.programCode,
          },
        });
        if (res.status === 200) {
          this.showNotification('success', ['Matching is in progress']);
        }
        this.refresh();
      } catch (error) {
        this.handleError(error);
      }
    },
    toggleConfirmation() {
      this.showConfirmation = !this.showConfirmation;
    },
  },
};
</script>

<style lang="sass" scoped>
  .card-container
    margin-left: auto
    margin-right: auto
  .document-container
    padding: 1rem 0
  .flex-item
    min-width: 206px
    min-height: 80px
    margin: 0rem 1rem 1rem 0rem
    text-align: center
  .flex-item-header
    min-width: 420px
  .grow
    flex-grow: 1
  .legend
    margin-top: 1rem
  .match-result-text
    font-weight: 700
    font-size: 12px
    padding-left: 0
  .panel-container
    padding: 1rem
    height: 100%
    flex-direction: column
    justify-content: center
  .panel-subtitle
    font-size: 12px
  .summary-panels
    margin-right: -1rem
  .wrap
    -webkit-flex-wrap: wrap
    flex-wrap: wrap
</style>
